import { config } from "@config/config";
import { ArrowPathIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { UseQueryResult, useQueryClient } from "@tanstack/react-query";
import { MEDICAL_NECESSITY_DSM5_CRITERIA } from "@utils/constants";
import { convertChecklistToString, convertStringToChecklist, unwrapConnection } from "@utils/utils";
import { format } from "date-fns";
import React, { useCallback, useEffect, useState } from "react";

import { Button, CheckboxList, Label, Paragraph, SlideOver, Small } from "@fronterahealth/frontera-ui-components";

import {
  ApiReportGoalAiGoalAttainmentScalePredictionStatusChoices,
  GetReportByIdQuery,
  ReportDomainType,
  ReportGoalType,
  useReportGoalPanelSingleViewQuery,
  useStartGoalAttainmentScaleWorkflowMutation,
} from "@api/graphql/types-and-hooks";
import { GasPanel } from "@components/GoalsSections/GasPanel";
import { TreatmentPlanForm, TreatmentPlanFormSkeleton } from "@components/TreatmentPlanFieldForm/TreatmentPlanForm";
import { useInlineTreatmentPlanEdit } from "@components/TreatmentPlanFieldForm/useInlineTreatmentPlanEdit";
import { useTreatmentPlanTemplate } from "@components/TreatmentPlanFieldForm/useTreatmentPlanTemplate";
import { goalToFieldValues } from "@components/TreatmentPlanFieldForm/utils";

interface ReportGoalPanelSingleViewProps {
  title: string;
  isPanelOpen: boolean;
  setPanelOpen: (open: boolean) => void;
  reportDomain: ReportDomainType | undefined;
  selectedGoal?: ReportGoalType | undefined;
  refetch: () => Promise<unknown>;
  reportQuery: UseQueryResult<GetReportByIdQuery, unknown>;
  isGasEnabled?: boolean;
  isMedicalNecessityEnabled?: boolean;
}

export const ReportGoalPanelSingleView: React.FC<ReportGoalPanelSingleViewProps> = ({
  isPanelOpen,
  setPanelOpen,
  title,
  selectedGoal,
  refetch,
  reportDomain,
  reportQuery,
  isGasEnabled = false,
  isMedicalNecessityEnabled = true,
}) => {
  const report = reportQuery?.data?.getReports?.edges?.[0]?.node;
  const { goalFields } = useTreatmentPlanTemplate(report?.id);
  const learnerId = report?.learner?.id || "";
  const queryClient = useQueryClient();

  // Medical Necessity state
  const [isMedicalNecessityChecked, setIsMedicalNecessityChecked] = useState(
    selectedGoal?.hasMedicalNecessity || false,
  );
  const [selectedCriteria, setSelectedCriteria] = useState<Record<string, boolean>>({});

  const goalId = selectedGoal?.id || "";

  const goalPanelQuery = useReportGoalPanelSingleViewQuery(
    { reportGoalId: goalId },
    {
      enabled: isPanelOpen && goalId.length > 0,
      queryKey: ["getReportGoal", goalId],
      refetchOnWindowFocus: true,
      refetchInterval: (query) => {
        const goal = query.state.data?.getReportGoal;
        if (!goal) return false;

        const isPending =
          goal.aiGoalAttainmentScalePredictionStatus ===
          ApiReportGoalAiGoalAttainmentScalePredictionStatusChoices.GoalPredictionPending;

        return isPending ? config.REPORT_POLLING_INTERVAL : false;
      },
    },
  );

  // Render only from the panel's own query, never the caller's `selectedGoal`.
  // The caller's copy can be partial (e.g. a create-mutation response without
  // customFields), and the WYSIWYG editor only reads its content on mount, so
  // mounting from it leaves template-seeded defaults blank until a remount.
  const goalData = goalPanelQuery.data?.getReportGoal;

  const invalidateGoalQuery = useCallback(() => {
    if (goalId) {
      queryClient.invalidateQueries({
        queryKey: ["getReportGoal", goalId],
      });
    }
    refetch();
  }, [queryClient, goalId, refetch]);

  const handleGoalMutationSuccess = () => {
    goalPanelQuery.refetch();
    refetch();
  };

  const { saveGoalFields, saveGoalCustomField, isSaving } = useInlineTreatmentPlanEdit({
    goalId: goalData?.id || goalId || undefined,
    reportId: report?.id || "",
    domainId: reportDomain?.id || "",
    learnerId,
    onSuccess: invalidateGoalQuery,
  });

  // Initialize medical necessity criteria from goalData
  // Reset state when opening panel for a new goal (goalData is undefined)
  useEffect(() => {
    if (goalData) {
      // Existing goal: sync state from goalData
      setIsMedicalNecessityChecked(goalData.hasMedicalNecessity || false);
      if (goalData.medicalNecessityCriteria) {
        const initialChecklist = convertStringToChecklist(
          goalData.medicalNecessityCriteria,
          MEDICAL_NECESSITY_DSM5_CRITERIA,
        );
        setSelectedCriteria(initialChecklist);
      } else {
        setSelectedCriteria({});
      }
    } else {
      // New goal: reset state to defaults
      setIsMedicalNecessityChecked(false);
      setSelectedCriteria({});
    }
  }, [goalData, isPanelOpen]);

  const handleMedicalNecessityToggle = async (checked: boolean) => {
    setIsMedicalNecessityChecked(checked);
    const result = await saveGoalFields({ hasMedicalNecessity: checked });
    if (!result.success) {
      // Revert the state change if save failed
      setIsMedicalNecessityChecked(!checked);
    }
  };

  const handleCriteriaChange = async (itemId: string, checked: boolean) => {
    const updatedCriteria = {
      ...selectedCriteria,
      [itemId]: checked,
    };
    setSelectedCriteria(updatedCriteria);

    const criteriaString = convertChecklistToString(updatedCriteria, MEDICAL_NECESSITY_DSM5_CRITERIA);
    const result = await saveGoalFields({
      medicalNecessityCriteria: criteriaString,
      hasMedicalNecessity: isMedicalNecessityChecked,
    });
    if (!result.success) {
      // Revert the state change if save failed
      setSelectedCriteria(selectedCriteria);
    }
  };

  const startGasWorkflowMutation = useStartGoalAttainmentScaleWorkflowMutation({});
  const gasStatus = goalData?.aiGoalAttainmentScalePredictionStatus;
  const isGasGenerating = gasStatus === ApiReportGoalAiGoalAttainmentScalePredictionStatusChoices.GoalPredictionPending;
  const isGasGenerated =
    gasStatus === ApiReportGoalAiGoalAttainmentScalePredictionStatusChoices.GoalPredictionPredicted;
  const isGasFailed = gasStatus === ApiReportGoalAiGoalAttainmentScalePredictionStatusChoices.Failure.valueOf();

  const handleGenerateGasValues = async () => {
    if (!goalData?.id) {
      return;
    }

    try {
      await startGasWorkflowMutation.mutateAsync({
        reportGoalId: goalData.id,
      });
      invalidateGoalQuery();
    } catch (error) {
      console.error("❌ Error starting GAS workflow:", error);
    }
  };

  const handleClosePanel = useCallback(
    (open: boolean) => {
      setPanelOpen(open);
      queryClient.removeQueries({
        queryKey: ["getReportGoal"],
        exact: false, // remove all goal panel queries
      });
    },
    [queryClient, setPanelOpen],
  );
  const GoalTitle = ({ title }: { title: string }) => {
    return (
      <div className="flex flex-row items-center justify-between gap-x-2">
        <Small displayType="normal" colorType="secondary">
          {title}
        </Small>
      </div>
    );
  };

  const baselineDataValue = goalData?.baselineData || "";
  const masteryCriteriaValue = goalData?.masteryCriteria || "";

  const hasBaselineData = baselineDataValue.trim().length > 0;
  const hasMasteryCriteria = masteryCriteriaValue.trim().length > 0;
  const canGenerate = hasBaselineData && hasMasteryCriteria;

  const renderGasPanel = () => {
    return (
      <>
        <GoalTitle title="Goal Attainment Scale" />
        <Paragraph size="extra-small" colorType="secondary">
          Define each GAS level for this goal. You can either complete all five levels manually or enter the Baseline
          (0) and Target Treatment (2) levels and let us generate the remaining levels for you. The Generate Values
          button will activate once Levels 0 and 2 are filled in.
        </Paragraph>
        <GasPanel
          GASData={{
            baselineData: goalData?.baselineData || "",
            masteryCriteria: goalData?.masteryCriteria || "",
            gasLevel1: goalData?.gasLevel1 || "",
            gasLevel3: goalData?.gasLevel3 || "",
            gasLevel4: goalData?.gasLevel4 || "",
          }}
          goalId={goalData?.id || goalId || undefined}
          reportId={report?.id || "<missing-report-id>"}
          domainId={reportDomain?.id || "<missing-domain-id>"}
          learnerId={learnerId || ""}
          onSubmitCallback={handleGoalMutationSuccess}
          goalName={goalData?.goalName || ""}
          isSidePanel={true}
          key={goalId || Date.now()}
        />
        <div className="flex flex-row justify-start items-center mb-4">
          {isGasFailed ? (
            <Small className="mt-4 flex flex-row items-center text-red-500!">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-500 inline mr-2" />
              Failed to Generate. Please try again.
            </Small>
          ) : (
            <div></div> // Empty div to maintain spacing when no error
          )}

          <Button
            data-testid="suggested-target-button"
            text={
              <div className="flex items-center">
                {isGasGenerating ? "Generating..." : isGasGenerated ? "Regenerate Values" : "Generate Values"}
              </div>
            }
            appearance={"text"}
            className={
              "text-green-700 hover:bg-white [&]:border-green-700! [&:hover]:border-green-700! focus:border-green-700! active:border-green-700!"
            }
            type="button"
            style={{ fontWeight: 400 }}
            disabled={!canGenerate || isGasGenerating || !goalData?.id}
            onClick={handleGenerateGasValues}
          />
        </div>
      </>
    );
  };

  const renderMedicalNecessitySection = () => {
    return (
      <div className="mt-6">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={isMedicalNecessityChecked}
            onChange={(e) => handleMedicalNecessityToggle(e.target.checked)}
            className="h-4 w-4 rounded-sm border-gray-300 text-hatch-600 focus:ring-hatch-600"
          />
          <span className="text-sm font-medium text-text-secondary">Include Medical Necessity</span>
        </label>
        {isMedicalNecessityChecked && (
          <div
            className={`border rounded-lg p-4 bg-white space-y-6 mt-2 ${!isMedicalNecessityChecked && "opacity-50"}`}
          >
            {MEDICAL_NECESSITY_DSM5_CRITERIA.map((section, index) => {
              const checklistItems = section.checklistItems.map((item, itemIndex) => {
                const isChecked = selectedCriteria[item.id] || false;
                return {
                  id: item.id,
                  title: `${itemIndex + 1}. ${item.title}`,
                  value: item.id,
                  checked: isChecked,
                  defaultChecked: isChecked,
                };
              });

              return (
                <div key={section.name}>
                  <Small colorType="primary">{`${String.fromCharCode(65 + index)}. ${section.name}`}</Small>
                  <div className="ml-6">
                    <CheckboxList
                      items={checklistItems}
                      title=""
                      legend={section.name}
                      orientation="vertical"
                      disabled={!isMedicalNecessityChecked}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const itemId = e.target.value;
                        handleCriteriaChange(itemId, e.target.checked);
                      }}
                      formNoValidate
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    if (goalPanelQuery.isLoading) {
      return <TreatmentPlanFormSkeleton count={Math.max(goalFields?.length || 0, 3)} />;
    }
    return (
      <div className="flex flex-col gap-y-2">
        <TreatmentPlanForm
          templateFields={goalFields}
          nativeFieldValues={goalData ? goalToFieldValues(goalData) : {}}
          onNativeFieldSave={saveGoalFields}
          customFields={unwrapConnection(goalData?.customFields) ?? []}
          onCustomFieldSave={saveGoalCustomField}
          onImageUploaded={invalidateGoalQuery}
          domain={reportDomain ?? null}
          fieldOverrides={{
            gas_level_1: null,
            gas_level_3: null,
            gas_level_4: null,
            ...(isGasEnabled ? { baseline_data: null, mastery_criteria: null } : {}),
          }}
        />
        {isGasEnabled && renderGasPanel()}
        {isMedicalNecessityEnabled && renderMedicalNecessitySection()}
      </div>
    );
  };

  const renderFooter = () => {
    return (
      <div className="flex justify-between items-center gap-2 shrink-0 border-t py-4 shadow-md px-6 bg-white border-limestone-100">
        <Label size="extra-small" colorType="secondary">
          {isSaving || goalPanelQuery.isLoading ? (
            <div className="flex flex-row items-center gap-x-2">
              <ArrowPathIcon className="h-4 w-4 animate-spin" /> {goalPanelQuery.isLoading ? "Loading..." : "Saving..."}
            </div>
          ) : goalData?.lastUpdatedDate || goalData?.createdDate ? (
            `Last saved ${format(new Date(goalData.lastUpdatedDate ?? goalData.createdDate), "h:mmaaa, M/d/yyyy")}`
          ) : null}
        </Label>
      </div>
    );
  };

  return (
    <div className="flex flex-col ">
      <SlideOver
        title={title}
        open={isPanelOpen}
        size="3xl"
        containerClassName="py-0"
        contentClassName="p-0! flex! flex-col! h-full! overflow-hidden!"
        setOpen={(open) => {
          if (isSaving) return;

          handleClosePanel(open);
        }}
      >
        <div className="flex flex-col h-full min-h-0">
          <div className="flex-1 overflow-y-auto py-4 px-6">{renderContent()}</div>
          {renderFooter()}
        </div>
      </SlideOver>
    </div>
  );
};
